/**
 * @fileoverview Syndicator Showdown — Share copy helpers
 * @module utils/shareCopy
 *
 * Pure, dependency-free copy builders used across Phase 6 share surfaces
 * (public stable share page, share-result cards/modals, challenge CTAs).
 * Deliberately avoids wagering language (bet/wager/punter/cash winnings) in
 * favour of "Virtual Winnings", "Stable Progress", "Build Your Stable" etc.
 *
 * Kept free of React/Firestore imports so it's trivially unit-testable —
 * see tests/web/shareCopy.test.js.
 */

function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  return `$${Math.round(Number(n)).toLocaleString('en-AU')}`;
}

function ordinal(n) {
  if (n === null || n === undefined) return null;
  const num = Number(n);
  if (Number.isNaN(num)) return null;
  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${num}th`;
  switch (num % 10) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
}

/**
 * Builds the share text used when a player shares their stable (before or
 * during a Showdown).
 *
 * @param {Object} params
 * @param {string} params.stableName - public name (never real displayName)
 * @param {string} [params.showdownName]
 * @param {number|null} [params.rank]
 * @param {number|null} [params.winnings]
 * @returns {string}
 */
export function buildStableShareText({ stableName, showdownName, rank, winnings } = {}) {
  const name = stableName || 'A Syndicator Showdown player';
  const showdown = showdownName || 'a Showdown';

  const extras = [];
  const rankStr = ordinal(rank);
  if (rankStr) extras.push(`currently ranked ${rankStr}`);
  const winningsStr = fmtMoney(winnings);
  if (winningsStr && winnings > 0) extras.push(`${winningsStr} in virtual winnings`);

  const extraStr = extras.length > 0 ? ` — ${extras.join(', ')}` : '';
  return `Check out ${name}'s stable in ${showdown} on Syndicator Showdown${extraStr}.`;
}

/**
 * Builds the share text for a race result (winner or placing).
 *
 * @param {Object} params
 * @param {string} params.stableName
 * @param {string} params.runnerName
 * @param {string} [params.placingText] - e.g. "1st", "2nd" — if omitted, derived from finishingPosition
 * @param {number} [params.finishingPosition]
 * @param {number|string} [params.raceNumber]
 * @param {string} [params.showdownName]
 * @param {number} [params.virtualPayout]
 * @returns {string}
 */
export function buildResultShareText({
  stableName, runnerName, placingText, finishingPosition, raceNumber, showdownName, virtualPayout,
} = {}) {
  const name = stableName || 'A Syndicator Showdown player';
  const runner = runnerName || 'Their runner';
  const showdown = showdownName ? ` in ${showdownName}` : '';
  const raceLabel = raceNumber ? `Race ${raceNumber}` : 'the race';
  const placing = placingText || ordinal(finishingPosition);
  const payoutStr = fmtMoney(virtualPayout);
  const payoutSuffix = payoutStr && virtualPayout > 0 ? ` Virtual Winnings: ${payoutStr}.` : '';

  if (finishingPosition === 1 || placing === '1st') {
    return `${name} had a winner! ${runner} won ${raceLabel}${showdown}.${payoutSuffix}`;
  }

  const placeStr = placing || 'in the money';
  return `${name}'s stable is on the board — ${runner} finished ${placeStr} in ${raceLabel}${showdown}.${payoutSuffix}`;
}

/**
 * Builds the share text for an overall leaderboard rank.
 *
 * @param {Object} params
 * @param {string} params.stableName
 * @param {number} params.rank
 * @param {string} [params.showdownName]
 * @param {number} [params.winnings]
 * @returns {string}
 */
export function buildLeaderboardShareText({ stableName, rank, showdownName, winnings } = {}) {
  const name = stableName || 'A Syndicator Showdown player';
  const rankStr = ordinal(rank) || 'unranked';
  const showdown = showdownName || 'their Showdown';
  const winningsStr = fmtMoney(winnings);
  const winningsSuffix = winningsStr && winnings > 0 ? ` — ${winningsStr} in virtual winnings` : '';
  return `${name} is ranked ${rankStr} in ${showdown}${winningsSuffix}.`;
}

/**
 * Builds the "Challenge a Friend" viral CTA copy shown on public share pages
 * and invite cards.
 *
 * @param {Object} params
 * @param {string} params.stableName
 * @param {string} [params.showdownName]
 * @returns {{ headline: string, body: string }}
 */
export function buildChallengeText({ stableName, showdownName } = {}) {
  const name = stableName || 'this stable';
  const showdown = showdownName ? ` in ${showdownName}` : '';
  return {
    headline: 'Think you can beat this stable?',
    body: `Build your stable and challenge ${name}${showdown}.`,
  };
}

export { fmtMoney, ordinal };
